/**
 * Forecasting Utilities for Customer Billing
 * 
 * Provides cost and usage forecasting based on historical data
 * Supports daily and weekly averaging with confidence metrics
 */

import { LLMCall } from '../API';
import { PeriodData, ForecastData, hasReliableForecastData, shouldExcludeWeekends, getBusinessDaysElapsed, getBusinessDays } from './periodCalculations';

export interface DailyAverages {
  cost: number;
  calls: number;
  standardCalls: number;
  premiumCalls: number;
  standardCost: number;
  premiumCost: number;
}

export interface ForecastOptions {
  excludeWeekends?: boolean;
  minimumDays?: number;
  methodology?: 'daily-average' | 'weekly-average';
}

/**
 * Calculate forecasted costs and usage for a partial period
 */
export async function calculateForecastedCosts(
  actualCalls: LLMCall[],
  period: PeriodData,
  options: ForecastOptions = {}
): Promise<ForecastData | null> {
  
  // Check if forecasting is applicable
  if (!period.isPartial || !hasReliableForecastData(period)) {
    return null;
  }
  
  const {
    excludeWeekends = shouldExcludeWeekends(period),
    minimumDays = 5,
    methodology = 'daily-average'
  } = options;
  
  // Calculate averages based on actual data
  const dailyAverages = calculateDailyAverages(actualCalls, period, excludeWeekends);
  
  // Determine confidence level based on data availability
  const confidenceLevel = calculateConfidenceLevel(period, actualCalls.length, excludeWeekends);
  
  // Calculate remaining time in period
  const remainingDays = calculateRemainingDays(period, excludeWeekends);
  
  return {
    projectedCost: dailyAverages.cost * remainingDays,
    projectedCalls: Math.round(dailyAverages.calls * remainingDays),
    confidenceLevel,
    methodology,
    basedOnDays: excludeWeekends 
      ? getBusinessDaysElapsed(period)
      : period.daysElapsed
  };
}

/**
 * Calculate daily averages from actual call data
 */
export function calculateDailyAverages(
  calls: LLMCall[],
  period: PeriodData,
  excludeWeekends: boolean = false
): DailyAverages {
  
  if (calls.length === 0) {
    return {
      cost: 0,
      calls: 0,
      standardCalls: 0,
      premiumCalls: 0,
      standardCost: 0,
      premiumCost: 0
    };
  }
  
  // Group calls by day for daily averaging
  const callsByDay = groupCallsByDay(calls, excludeWeekends);
  const activeDays = Object.keys(callsByDay).length;
  
  if (activeDays === 0) {
    return {
      cost: 0,
      calls: 0,
      standardCalls: 0,
      premiumCalls: 0,
      standardCost: 0,
      premiumCost: 0
    };
  }
  
  // Calculate totals
  let totalCost = 0;
  let totalCalls = 0;
  let totalStandardCalls = 0;
  let totalPremiumCalls = 0;
  let totalStandardCost = 0;
  let totalPremiumCost = 0;
  
  Object.values(callsByDay).forEach(dayCalls => {
    dayCalls.forEach(call => {
      const callCost = calculateCallCost(call);
      const tier = getModelTier(call);
      
      totalCost += callCost;
      totalCalls++;
      
      if (tier === 'premium') {
        totalPremiumCalls++;
        totalPremiumCost += callCost;
      } else {
        totalStandardCalls++;
        totalStandardCost += callCost;
      }
    });
  });
  
  // Return daily averages
  return {
    cost: totalCost / activeDays,
    calls: totalCalls / activeDays,
    standardCalls: totalStandardCalls / activeDays,
    premiumCalls: totalPremiumCalls / activeDays,
    standardCost: totalStandardCost / activeDays,
    premiumCost: totalPremiumCost / activeDays
  };
}

/**
 * Group calls by day, optionally excluding weekends
 */
function groupCallsByDay(calls: LLMCall[], excludeWeekends: boolean): Record<string, LLMCall[]> {
  const callsByDay: Record<string, LLMCall[]> = {};
  
  calls.forEach(call => {
    const callDate = new Date(call.createdAt);
    const dayOfWeek = callDate.getUTCDay();
    
    // Skip weekends if requested
    if (excludeWeekends && (dayOfWeek === 0 || dayOfWeek === 6)) {
      return;
    }
    
    const dayKey = callDate.toISOString().split('T')[0]; // YYYY-MM-DD format
    
    if (!callsByDay[dayKey]) {
      callsByDay[dayKey] = [];
    }
    
    callsByDay[dayKey].push(call);
  });
  
  return callsByDay;
}

/**
 * Calculate confidence level for forecasting (0-1 scale)
 * Based on amount of historical data and pattern consistency
 */
function calculateConfidenceLevel(
  period: PeriodData,
  callCount: number,
  excludeWeekends: boolean
): number {
  
  // Base confidence on days of data available
  const daysOfData = excludeWeekends 
    ? getBusinessDaysElapsed(period)
    : period.daysElapsed;
  
  let baseConfidence = 0;
  
  // Days-based confidence
  if (daysOfData >= 14) {
    baseConfidence = 0.9; // Very high confidence with 2+ weeks
  } else if (daysOfData >= 7) {
    baseConfidence = 0.8; // High confidence with 1+ week
  } else if (daysOfData >= 5) {
    baseConfidence = 0.7; // Good confidence with 5+ days
  } else if (daysOfData >= 3) {
    baseConfidence = 0.5; // Medium confidence with 3-4 days
  } else if (daysOfData >= 1) {
    baseConfidence = 0.3; // Low confidence with 1-2 days
  } else {
    baseConfidence = 0.1; // Very low confidence
  }
  
  // Adjust based on call volume (more calls = more reliable)
  let volumeMultiplier = 1;
  if (callCount >= 100) {
    volumeMultiplier = 1.1; // Boost for high volume
  } else if (callCount >= 50) {
    volumeMultiplier = 1.05; // Slight boost for medium volume
  } else if (callCount < 10) {
    volumeMultiplier = 0.8; // Reduce for very low volume
  }
  
  // Apply period-specific adjustments
  let periodMultiplier = 1;
  switch (period.type) {
    case 'today':
      // Intraday forecasting is less reliable
      periodMultiplier = 0.7;
      break;
    case 'current-week':
      // Weekly patterns are fairly stable
      periodMultiplier = 1;
      break;
    case 'current-month':
      // Monthly patterns can vary more
      periodMultiplier = 0.9;
      break;
  }
  
  const finalConfidence = Math.min(1, baseConfidence * volumeMultiplier * periodMultiplier);
  return Math.round(finalConfidence * 100) / 100; // Round to 2 decimal places
}

/**
 * Calculate remaining days in period for forecasting
 */
function calculateRemainingDays(period: PeriodData, excludeWeekends: boolean): number {
  if (!period.isPartial) {
    return 0;
  }
  
  if (excludeWeekends) {
    const totalBusinessDays = getBusinessDays(period.startDate, period.endDate);
    const elapsedBusinessDays = getBusinessDaysElapsed(period);
    return Math.max(0, totalBusinessDays - elapsedBusinessDays);
  } else {
    return Math.max(0, period.totalDays - period.daysElapsed);
  }
}

/**
 * Calculate the cost of an individual call
 * Uses service pricing if available, falls back to model pricing
 */
export function calculateCallCost(call: LLMCall): number {
  const inputTokens = call.tokenCountPrompt || 0;
  const outputTokens = call.tokenCountResponse || 0;
  
  // Priority 1: Service pricing (if call has a service)
  if (call.service?.inputTokenPricePerMillion && call.service?.outputTokenPricePerMillion) {
    const inputCost = (inputTokens / 1_000_000) * call.service.inputTokenPricePerMillion;
    const outputCost = (outputTokens / 1_000_000) * call.service.outputTokenPricePerMillion;
    return inputCost + outputCost;
  }
  
  // Priority 2: Model pricing (if call has a model with pricing)
  if (call.model?.inputTokenPrice && call.model?.outputTokenPrice) {
    const isBatch = call.batch === true;
    const inputRate = isBatch && call.model.inputTokenBatchPrice != null
      ? call.model.inputTokenBatchPrice
      : call.model.inputTokenPrice;
    const outputRate = isBatch && call.model.outputTokenBatchPrice != null
      ? call.model.outputTokenBatchPrice
      : call.model.outputTokenPrice;
    const inputCost = (inputTokens / 1_000_000) * inputRate;
    const outputCost = (outputTokens / 1_000_000) * outputRate;
    return inputCost + outputCost;
  }
  
  // No pricing information available - calculate estimated cost
  // This is a fallback when pricing data is missing
  console.warn(`No pricing information available for call ${call.id}`);
  return 0;
}

/**
 * Determine model tier (standard vs premium) for billing categorization
 * Uses service group hierarchy as primary classification method
 */
export function getModelTier(call: LLMCall): 'standard' | 'premium' {
  // Priority 1: Check if call has a service and the service has a group
  if (call.service?.group?.name) {
    const groupName = call.service.group.name.toLowerCase();
    
    // Check for premium tier indicators
    if (groupName.includes('premium') || 
        groupName.includes('reasoning') || 
        groupName.includes('superpremium')) {
      return 'premium';
    }
    return 'standard';
  }
  
  // Priority 2: Check output token pricing (>$5 per million = premium)
  const outputTokenPrice = call.service?.outputTokenPricePerMillion || call.model?.outputTokenPrice;
  if (outputTokenPrice && outputTokenPrice > 5) {
    return 'premium';
  }
  
  // Priority 3: Fallback to model's defaultServiceGroup
  if (call.model?.defaultServiceGroup) {
    const defaultGroup = call.model.defaultServiceGroup.toLowerCase();
    
    // Check for premium tier indicators in model's default service group
    if (defaultGroup.includes('premium') || 
        defaultGroup.includes('reasoning') || 
        defaultGroup.includes('superpremium')) {
      return 'premium';
    }
  }
  
  // Default to standard if no classification found
  return 'standard';
}

/**
 * Calculate billing metrics with forecasting for a set of calls
 */
export interface BillingMetrics {
  actualCost: number;
  actualCalls: number;
  standardCalls: number;
  premiumCalls: number;
  standardCost: number;
  premiumCost: number;
  forecast?: ForecastData;
  totalProjectedCost?: number; // actual + forecasted
  totalProjectedCalls?: number; // actual + forecasted
}

export async function calculateBillingMetrics(
  calls: LLMCall[],
  period: PeriodData,
  forecastOptions?: ForecastOptions
): Promise<BillingMetrics> {
  
  // Calculate actual metrics
  let actualCost = 0;
  let standardCalls = 0;
  let premiumCalls = 0;
  let standardCost = 0;
  let premiumCost = 0;
  
  calls.forEach(call => {
    const callCost = calculateCallCost(call);
    const tier = getModelTier(call);
    
    actualCost += callCost;
    
    if (tier === 'premium') {
      premiumCalls++;
      premiumCost += callCost;
    } else {
      standardCalls++;
      standardCost += callCost;
    }
  });
  
  const result: BillingMetrics = {
    actualCost,
    actualCalls: calls.length,
    standardCalls,
    premiumCalls,
    standardCost,
    premiumCost
  };
  
  // Add forecasting if applicable
  if (period.isPartial) {
    const forecast = await calculateForecastedCosts(calls, period, forecastOptions);
    if (forecast) {
      result.forecast = forecast;
      result.totalProjectedCost = actualCost + forecast.projectedCost;
      result.totalProjectedCalls = calls.length + forecast.projectedCalls;
    }
  }
  
  return result;
}

/**
 * Format confidence level for display
 */
export function formatConfidenceLevel(confidence: number): string {
  if (confidence >= 0.8) return 'High';
  if (confidence >= 0.6) return 'Medium';
  if (confidence >= 0.4) return 'Low';
  return 'Very Low';
}

/**
 * Get forecast disclaimer text based on confidence and methodology
 */
export function getForecastDisclaimer(forecast: ForecastData): string {
  const confidenceText = formatConfidenceLevel(forecast.confidenceLevel);
  const daysText = forecast.basedOnDays === 1 ? '1 day' : `${forecast.basedOnDays} days`;
  
  return `Forecast based on ${daysText} of data (${confidenceText} confidence)`;
}
